import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../config/firebase/auth';
import { 
  RestaurantContextType, 
  RestaurantContextState,
  Restaurant,
  UserRole
} from '../types/restaurant';
import * as restaurantService from '../services/restaurant/restaurantService';
import { db } from '../config/firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  hasPermission as checkPermission,
  canManageStaff,
  canManageSettings,
  canManageMenu,
  canManageTables,
  canViewReports,
  canManageInventory
} from '../utils/permissions';

// Utility function to check if running in development
const isDevelopment = process.env.NODE_ENV === 'development';

// Default context functions that throw errors when called outside provider
const throwProviderError = (functionName: string) => {
  throw new Error(`${functionName} must be called within a RestaurantProvider`);
};

const initialState: RestaurantContextState = {
  user: null,
  userProfile: null,
  restaurant: null,
  isLoading: true,
  error: null
};

const RestaurantContext = createContext<RestaurantContextType>({
  ...initialState,
  userRole: null,
  refreshRestaurantData: () => throwProviderError('refreshRestaurantData'),
  updateRestaurantSettings: () => throwProviderError('updateRestaurantSettings'),
  updateRestaurantName: () => throwProviderError('updateRestaurantName'),
  setRestaurantLogo: () => throwProviderError('setRestaurantLogo'),
  addStaffMember: () => throwProviderError('addStaffMember'),
  toggleRestaurantActive: () => throwProviderError('toggleRestaurantActive'),
  removeStaffMember: () => throwProviderError('removeStaffMember'),
  updateStaffRole: () => throwProviderError('updateStaffRole'),
  hasPermission: () => throwProviderError('hasPermission'),
  canManageStaff: () => throwProviderError('canManageStaff'),
  canManageSettings: () => throwProviderError('canManageSettings'),
  canManageMenu: () => throwProviderError('canManageMenu'),
  canManageTables: () => throwProviderError('canManageTables'),
  canViewReports: () => throwProviderError('canViewReports'),
  canManageInventory: () => throwProviderError('canManageInventory')
});

export const RestaurantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<RestaurantContextState>(initialState);
  const unsubscribeSnapshot = useRef<(() => void) | null>(null);

  const fetchRestaurantData = useCallback(async (user: User) => {
    try {
      const userProfile = await restaurantService.getUserProfile(user.uid);

      // Check if user is super admin (by email)
      const SUPER_ADMIN_EMAILS = ['yappppp.adm@gmail.com'];
      const isSuperAdmin = SUPER_ADMIN_EMAILS.includes(user.email || '') || userProfile?.role === 'super_admin';

      if (!userProfile) {
        throw new Error("No se encontró el perfil del usuario.");
      }

      // Super admins don't need a restaurantId
      if (!isSuperAdmin && !userProfile.restaurantId) {
        throw new Error("El perfil de usuario no contiene restaurantId.");
      }

      // For super admins, just set the user profile without restaurant data
      if (isSuperAdmin) {
        setState(prev => ({
          ...prev,
          userProfile,
          restaurant: null, // Super admins don't have a specific restaurant
          error: null
        }));
        return;
      }

      // Cleanup previous subscription if it exists
      unsubscribeSnapshot.current?.();

      const docRef = doc(db, 'restaurants', userProfile.restaurantId);
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          
          // Validate timestamps
          const createdAt = data.createdAt?.toDate?.();
          const updatedAt = data.updatedAt?.toDate?.();
          
          if (!createdAt || !updatedAt) {
            console.warn('Restaurant document missing timestamp data:', {
              restaurantId: docSnap.id,
              hasCreatedAt: !!createdAt,
              hasUpdatedAt: !!updatedAt
            });
          }

          setState(prev => ({
            ...prev,
            userProfile,
            restaurant: {
              id: docSnap.id,
              name: data.name,
              logoUrl: data.logoUrl,
              settings: data.settings ?? prev.restaurant?.settings ?? {},
              active: data.active ?? true,
              createdAt: createdAt || new Date(),
              updatedAt: updatedAt || new Date(),
              // POS Integration (Super Admin Only)
              integrationMode: data.integrationMode ?? prev.restaurant?.integrationMode ?? 'none',
              posConfig: data.posConfig ?? prev.restaurant?.posConfig,
              // Payment Processor Configuration
              processor: data.processor ?? prev.restaurant?.processor,
              azulConfig: data.azulConfig ?? prev.restaurant?.azulConfig,
              cardnetConfig: data.cardnetConfig ?? prev.restaurant?.cardnetConfig,
              stripeConfig: data.stripeConfig ?? prev.restaurant?.stripeConfig,
            },
            error: null
          }));

          if (isDevelopment) {
            console.log('✅ Restaurant set in context (real-time):', data);
          }
        }
      });

      unsubscribeSnapshot.current = unsubscribe;
      await restaurantService.updateUserLastLogin(user.uid);
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Error desconocido',
        userProfile: null,
        restaurant: null
      }));
    }
  }, []);

  // Cleanup subscription on unmount
  useEffect(() => {
    return () => {
      unsubscribeSnapshot.current?.();
    };
  }, []);

  const refreshRestaurantData = useCallback(async () => {
    if (!state.user) return;
    await fetchRestaurantData(state.user);
  }, [state.user, fetchRestaurantData]);

  // Permission check functions
  const hasPermission = useCallback((requiredRole: UserRole): boolean => {
    return checkPermission(state.userProfile?.role ?? null, requiredRole);
  }, [state.userProfile]);

  const canManageStaffCheck = useCallback((): boolean => {
    return canManageStaff(state.userProfile?.role ?? null);
  }, [state.userProfile]);

  const canManageSettingsCheck = useCallback((): boolean => {
    return canManageSettings(state.userProfile?.role ?? null);
  }, [state.userProfile]);

  const canManageMenuCheck = useCallback((): boolean => {
    return canManageMenu(state.userProfile?.role ?? null);
  }, [state.userProfile]);

  const canManageTablesCheck = useCallback((): boolean => {
    return canManageTables(state.userProfile?.role ?? null);
  }, [state.userProfile]);

  const canViewReportsCheck = useCallback((): boolean => {
    return canViewReports(state.userProfile?.role ?? null);
  }, [state.userProfile]);

  const canManageInventoryCheck = useCallback((): boolean => {
    return canManageInventory(state.userProfile?.role ?? null);
  }, [state.userProfile]);

  const updateRestaurantSettings = useCallback(async (settings: Partial<Restaurant['settings']>) => {
    if (!state.restaurant || !state.user || !state.userProfile) return;
    
    if (!state.restaurant.id) {
      throw new Error('Restaurant ID not found');
    }
    
    try {
      await restaurantService.updateRestaurantSettings(
        state.restaurant.id,
        settings,
        state.user.uid,
        state.userProfile.email,
        state.userProfile.role
      );
      await refreshRestaurantData();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Error al actualizar configuración'
      }));
    }
  }, [state.restaurant, state.user, state.userProfile, refreshRestaurantData]);


  const updateRestaurantName = useCallback(async (name: string) => {
    if (!state.restaurant || !state.user || !state.userProfile) return;

    if (!state.restaurant.id) {
      throw new Error('Restaurant ID not found');
    }

    try {
      await restaurantService.updateRestaurantName(
        state.restaurant.id,
        name,
        state.user.uid,
        state.userProfile.email,
        state.userProfile.role
      );
      await refreshRestaurantData();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Error al actualizar el nombre del restaurante'
      }));
      throw error;
    }
  }, [state.restaurant, state.user, state.userProfile, refreshRestaurantData]);

  const setRestaurantLogo = useCallback(async (logoUrl: string) => {
    if (!state.restaurant || !state.user || !state.userProfile) return;

    if (!state.restaurant.id) {
      throw new Error('Restaurant ID not found');
    }

    try {
      await restaurantService.setRestaurantLogo(
        state.restaurant.id,
        logoUrl,
        state.user.uid,
        state.userProfile.email,
        state.userProfile.role
      );
      await refreshRestaurantData();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Error al actualizar el logo'
      }));
    }
  }, [state.restaurant, state.user, state.userProfile, refreshRestaurantData]);

  const addStaffMember = useCallback(async (email: string, role: UserRole) => {
    if (!state.restaurant || !state.user || !state.userProfile) return;
    
    if (!state.restaurant.id) {
      throw new Error('Restaurant ID not found');
    }
    
    try {
      await restaurantService.addStaffMember(
        state.restaurant.id,
        email,
        role,
        state.user.uid,
        state.userProfile.email,
        state.userProfile.role
      );
      await refreshRestaurantData();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Error al agregar miembro del personal'
      }));
    }
  }, [state.restaurant, state.user, state.userProfile, refreshRestaurantData]);

  const toggleRestaurantActive = useCallback(async (active: boolean) => {
    if (!state.restaurant || !state.user || !state.userProfile) return;
    
    if (!state.restaurant.id) {
      throw new Error('Restaurant ID not found');
    }
    
    try {
      await restaurantService.toggleRestaurantActive(
        state.restaurant.id,
        active,
        state.user.uid,
        state.userProfile.email,
        state.userProfile.role
      );
      await refreshRestaurantData();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Error al cambiar estado del restaurante'
      }));
    }
  }, [state.restaurant, state.user, state.userProfile, refreshRestaurantData]);

  const removeStaffMember = useCallback(async (userId: string) => {
    if (!state.restaurant || !state.user || !state.userProfile) return;
    
    if (!state.restaurant.id) {
      throw new Error('Restaurant ID not found');
    }
    
    try {
      await restaurantService.removeStaffMember(
        state.restaurant.id,
        userId,
        state.user.uid,
        state.userProfile.email,
        state.userProfile.role
      );
      await refreshRestaurantData();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Error al remover miembro del personal'
      }));
    }
  }, [state.restaurant, state.user, state.userProfile, refreshRestaurantData]);

  const updateStaffRole = useCallback(async (userId: string, newRole: UserRole) => {
    if (!state.restaurant || !state.user || !state.userProfile) return;
    
    if (!state.restaurant.id) {
      throw new Error('Restaurant ID not found');
    }
    
    try {
      await restaurantService.updateStaffRole(
        state.restaurant.id,
        userId,
        newRole,
        state.user.uid,
        state.userProfile.email,
        state.userProfile.role
      );
      await refreshRestaurantData();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Error al actualizar rol del personal'
      }));
    }
  }, [state.restaurant, state.user, state.userProfile, refreshRestaurantData]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setState(prev => ({ ...prev, user, isLoading: true }));
      
      if (!user) {
        setState(prev => ({ ...prev, userProfile: null, restaurant: null, isLoading: false }));
        return;
      }

      await fetchRestaurantData(user);
      setState(prev => ({ ...prev, isLoading: false }));
    });

    return () => unsubscribe();
  }, [fetchRestaurantData]);

  const value: RestaurantContextType = {
    ...state,
    userRole: state.userProfile?.role ?? (state.user?.email === 'yappppp.adm@gmail.com' ? 'super_admin' : null),
    refreshRestaurantData,
    updateRestaurantSettings,
    updateRestaurantName,
    setRestaurantLogo,
    addStaffMember,
    toggleRestaurantActive,
    removeStaffMember,
    updateStaffRole,
    hasPermission,
    canManageStaff: canManageStaffCheck,
    canManageSettings: canManageSettingsCheck,
    canManageMenu: canManageMenuCheck,
    canManageTables: canManageTablesCheck,
    canViewReports: canViewReportsCheck,
    canManageInventory: canManageInventoryCheck
  };

  return (
    <RestaurantContext.Provider value={value}>
      {children}
    </RestaurantContext.Provider>
  );
};

export const useRestaurant = () => {
  const context = useContext(RestaurantContext);
  if (!context) {
    throw new Error('useRestaurant must be used within a RestaurantProvider');
  }
  return context;
};